import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Calendar,
  Clock,
  MapPin,
  Heart,
  Truck,
  Coffee,
  IceCream,
  Wheat,
  Palette,
  PartyPopper,
  ShoppingBag,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import QuantityStepper from "../components/QuantityStepper";
import CheckoutView from "../components/CheckoutView";
import { useToast } from "../hooks/use-toast";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Truck, Coffee, IceCream, Wheat, Palette, PartyPopper };
const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

const ParkingEvent = () => {
  const { toast } = useToast();
  const params = useParams();
  const slug = params.slug || "raf-valley-family-day";
  const [ev, setEv] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(0);
  const [donation, setDonation] = useState(0);
  const [customDonation, setCustomDonation] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/events/${slug}`)
      .then((r) => {
        setEv(r.data);
        setLoadState("ready");
      })
      .catch(() => setLoadState("notfound"));
  }, [slug]);

  const parkingTotal = qty * (ev?.parking?.price || 0);
  const donationTotal =
    (Number(customDonation) || 0) + (customDonation ? 0 : donation);
  const total = parkingTotal + donationTotal;

  const inputCls =
    "w-full bg-white border border-[#d9c3a8] px-4 py-2.5 rounded-sm outline-none focus:ring-2 focus:ring-[#ebc754] text-[#1e231f]";

  if (loadState !== "ready") {
    return (
      <div className="bg-[#f2e5de] min-h-screen">
        <Navbar />
        <div className="feral-texture min-h-[60vh] flex items-center justify-center px-6 pt-28">
          <p className="text-[#6e5646]" data-testid="event-load-state">
            {loadState === "loading" ? "Loading event…" : "Event not found."}
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  if (showCheckout) {
    const checkoutLines = [
      { name: ev.parking.name, qty, price: ev.parking.price, meta: ev.dateLine },
      ...(donationTotal > 0
        ? [{ name: `Donation to ${ev.charity}`, qty: 1, price: donationTotal }]
        : []),
    ];
    return (
      <CheckoutView
        event={slug}
        items={[{ id: "parking", qty }]}
        donation={donationTotal}
        eventName={ev.name}
        dateLine={ev.dateLine}
        lines={checkoutLines}
        total={total}
        showReg
        onBack={() => setShowCheckout(false)}
      />
    );
  }

  return (
    <div className="bg-[#f2e5de] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="feral-texture pt-28 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/events"
            className="inline-flex items-center gap-1 text-[#9b3d0a] hover:gap-2 transition-all text-sm font-semibold mb-6"
          >
            <ChevronLeft size={18} /> All events
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Gallery */}
            <div>
              <div className="rounded-md overflow-hidden shadow-xl aspect-[3/2]">
                <img src={ev.images[activeImg]} alt={ev.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex gap-3 mt-3">
                {ev.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-24 h-16 rounded overflow-hidden border-2 transition-colors ${
                      i === activeImg ? "border-[#9b3d0a]" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Details */}
            <div>
              <p className="text-[#9b3d0a] tracking-[0.18em] text-xs font-semibold uppercase mb-3">
                {ev.category}
              </p>
              <h1 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-2">{ev.name}</h1>
              <p className="text-[#6e5646] text-lg mb-6">{ev.subtitle}</p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-[#2e322c]">
                  <Calendar size={18} className="text-[#ebc754]" />
                  <span>{ev.dateLine}</span>
                </div>
                <div className="flex items-center gap-3 text-[#2e322c]">
                  <Clock size={18} className="text-[#ebc754]" />
                  <span>{ev.time}</span>
                </div>
                <div className="flex items-center gap-3 text-[#2e322c]">
                  <MapPin size={18} className="text-[#ebc754]" />
                  <span>{ev.venue}</span>
                </div>
              </div>

              {/* Charity highlight */}
              <div className="flex items-start gap-3 bg-[#9b3d0a] feral-texture-dark rounded-md p-4 text-[#f2e5de]">
                <Heart size={20} className="text-[#e5816a] mt-0.5 shrink-0" />
                <p className="text-sm leading-relaxed">
                  All profits from this event go to{" "}
                  <span className="font-semibold">{ev.charity}</span>. Thank you for
                  helping us look out for one another.
                </p>
              </div>

              <a
                href="#book"
                className="inline-block mt-6 bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] px-10 py-3.5 tracking-[0.15em] text-sm font-semibold transition-colors duration-300"
              >
                BOOK PARKING
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="feral-texture px-6 pb-12">
        <div className="max-w-3xl mx-auto space-y-5 text-[#1e231f] text-lg leading-relaxed">
          {ev.description.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      {/* What's on */}
      <section className="feral-texture px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="feral-serif text-[#9b3d0a] text-3xl text-center mb-8">
            What's on
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            {ev.whatsOn.map((w) => {
              const Ic = ICONS[w.icon] || PartyPopper;
              return (
                <div
                  key={w.label}
                  className="bg-white border border-[#d9c3a8] rounded-md p-5 flex flex-col items-center text-center gap-3"
                >
                  <span className="w-12 h-12 rounded-full bg-[#f0dbbe] text-[#ebc754] flex items-center justify-center">
                    <Ic size={22} />
                  </span>
                  <span className="text-[#2e322c] text-sm font-medium">{w.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Booking */}
      <section id="book" className="feral-texture bg-[#e2c7b0] py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold mb-3">
              BOOK YOUR PARKING
            </p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl">Reserve your space</h2>
          </div>

          {/* Parking */}
          <div className="bg-white border border-[#d9c3a8] rounded-md p-6 mb-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-[#1e231f] font-semibold">{ev.parking.name}</h3>
                <p className="text-[#6e5646] text-sm mt-1.5 leading-relaxed">
                  {ev.parking.desc}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="feral-serif text-[#9b3d0a] text-2xl leading-none">
                  {gbp(ev.parking.price)}
                </p>
                <p className="text-[#9a876f] text-xs mt-1">/ {ev.parking.unit}</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-[#9a876f]">
                {qty > 0 ? `${gbp(parkingTotal)} total` : "\u00a0"}
              </span>
              <QuantityStepper value={qty} onChange={setQty} testId="qty-parking" />
            </div>
          </div>

          {/* Donation */}
          <div className="bg-white border border-[#d9c3a8] rounded-md p-6 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Heart size={18} className="text-[#9b3d0a]" />
              <h3 className="text-[#1e231f] font-semibold">Add a donation</h3>
            </div>
            <p className="text-[#6e5646] text-sm mb-4 leading-relaxed">{ev.donationNote}</p>
            <div className="flex flex-wrap gap-2.5">
              {ev.donationPresets.map((d) => {
                const active = !customDonation && donation === d;
                return (
                  <button
                    key={d}
                    onClick={() => {
                      setDonation(d);
                      setCustomDonation("");
                    }}
                    className={`px-4 py-2 rounded-sm text-sm font-semibold border transition-colors ${
                      active
                        ? "bg-[#9b3d0a] border-[#9b3d0a] text-[#f2e5de]"
                        : "bg-white border-[#d9c3a8] text-[#2e322c] hover:border-[#9b3d0a]"
                    }`}
                  >
                    {d === 0 ? "No thanks" : `+${gbp(d)}`}
                  </button>
                );
              })}
              <div className="flex items-center gap-1.5 border border-[#d9c3a8] rounded-sm px-3">
                <span className="text-[#9a876f]">£</span>
                <input
                  type="number"
                  min="0"
                  placeholder="Other"
                  value={customDonation}
                  onChange={(e) => setCustomDonation(e.target.value)}
                  className="w-20 py-2 outline-none text-[#1e231f] bg-transparent"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-white rounded-md shadow-lg border border-[#d9c3a8] overflow-hidden">
            <div className="bg-[#9b3d0a] px-6 py-4 flex items-center gap-3">
              <ShoppingBag size={20} className="text-[#f2e5de]" />
              <h3 className="text-[#ebc754] font-semibold tracking-[0.12em] text-sm uppercase">
                Your Order
              </h3>
            </div>
            <div className="p-6">
              <div className="flex justify-between text-[#2e322c] mb-2">
                <span>Parking × {qty}</span>
                <span>{gbp(parkingTotal)}</span>
              </div>
              <div className="flex justify-between text-[#2e322c] mb-4">
                <span>Donation to {ev.charity}</span>
                <span>{gbp(donationTotal)}</span>
              </div>
              <div className="border-t border-[#d9c3a8] pt-4 flex items-center justify-between">
                <span className="text-[#1e231f] font-semibold">Total</span>
                <span className="feral-serif text-[#9b3d0a] text-2xl">{gbp(total)}</span>
              </div>
              <button
                disabled={qty === 0}
                onClick={() => setShowCheckout(true)}
                className="w-full mt-5 bg-[#9b3d0a] hover:bg-[#7e3208] disabled:opacity-40 disabled:cursor-not-allowed text-[#f2e5de] py-3.5 rounded-full tracking-[0.15em] text-sm font-semibold transition-colors duration-300"
              >
                CHECKOUT
              </button>
              <p className="text-[11px] text-center mt-3 text-[#9a876f]">
                100% of profits donated to {ev.charity}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ParkingEvent;
